import React, { ReactNode, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Dashboard, DashboardVariables, Dashboard_dashboard } from '../../queries/types/Dashboard'
import { dashboardQuery, getAllAppsQuery } from '../../queries'
import { ExpiredIcon, FailedIcon, IconProps, InvoiceIcon, MoneyIcon, PendingIcon, ProfitIcon, ReceiptIcon, RefundIcon, SpinnerIcon } from '../icons'
import { AllApps } from '../../queries/types/AllApps'
import { endOfDay, format, subDays } from 'date-fns'
import AppsDropdown from '../products/apps-dropdown'
import { startOfDay } from 'date-fns/esm'
import { PrimaryButton } from '../button'
import { EmptyState, LoadingRows, PageHeader } from '../ui'
import { useTranslation } from 'react-i18next'
import { isNetworkRequestInFlight } from '@apollo/client/core/networkStatus'
import '../../utils'
import { App } from '../../types/global'

interface Props {
    title: string
    icon: React.ComponentType<IconProps>
    iconColor?: string
    children: ReactNode
}

function Block(props: Props): JSX.Element {
    const Icon = props.icon
    return (
        <div className='w-full md:w-1/2 lg:w-1/4 p-3'>
            <div className='bg-white border rounded shadow-sm p-2'>
                <div className='flex flex-row items-center'>
                    <div className='flex-shrink pl-2 pr-4'><Icon size={3} color={props.iconColor} /></div>
                    <div className='flex-1 text-right md:text-center'>
                        <h5 className='font-bold uppercase text-gray-500'>{props.title}</h5>
                        <h3 className='font-bold text-3xl'>{props.children}</h3>
                    </div>
                </div>
            </div>
        </div>
    )
}

function formatInputDateString(date: Date) {
    return format(date, 'yyyy-MM-dd')
}

export function Home(): JSX.Element {
    const { t } = useTranslation()
    const { data: apps } = useQuery<AllApps>(getAllAppsQuery)
    const [ app, setApp ] = useState<App|null>(null)
    const [ dateBegin, setDateBegin ] = useState<Date>(startOfDay(subDays(new Date(), 7)))
    const [ dateEnd, setDateEnd ] = useState<Date>(endOfDay(new Date()))
    const query = useQuery<Dashboard, DashboardVariables>(dashboardQuery, {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
            appID: app?.id,
            begin: dateBegin,
            end: dateEnd
        }
    })

    useEffect(() => {
        query.refetch({
            appID: app?.id,
            begin: dateBegin,
            end: dateEnd
        })
    }, [app, dateBegin, dateEnd])

    const overview = query.data?.dashboard

    function blocks(o: Dashboard_dashboard) {
        return (<>
            <h5 className='text-xl font-medium leading-tight text-gray-800 mb-5'>{t('dashboard.titles.money')}</h5>
            <div className='flex flex-wrap'>
                <Block title={t('dashboard.stats.profit')} icon={ProfitIcon} iconColor='green'>
                    {((o.revenueOfPaid ?? 0) * 0.85).toCurrency()}
                </Block>
                <Block title={t('dashboard.stats.revenue')} icon={MoneyIcon} iconColor='orange'>
                    {(o.revenueOfPaid ?? 0).toCurrency()}
                </Block>
            </div>
            <h5 className='text-xl font-medium leading-tight text-gray-800 mt-5 mb-5'>{t('dashboard.titles.orders')}</h5>
            <div className='flex flex-wrap'>
                <Block title={t('dashboard.stats.total')} icon={InvoiceIcon}>
                    {(o.numOfOrders ?? 0).formatted()}
                </Block>
                <Block title={t('dashboard.stats.paid')} icon={ReceiptIcon} iconColor='green'>
                    {(o.numOfPaidOrders ?? 0).formatted()}
                </Block>
                <Block title={t('dashboard.stats.refunded')} icon={RefundIcon} iconColor='orange'>
                    {(o.numOfRefundedOrders ?? 0).formatted()}
                </Block>
                <Block title={t('dashboard.stats.pending')} icon={PendingIcon} iconColor='orange'>
                    {(o.numOfPendingOrders ?? 0).formatted()}
                </Block>
                <Block title={t('dashboard.stats.expired')} icon={ExpiredIcon} iconColor='orange'>
                    {(o.numOfExpiredOrders ?? 0).formatted()}
                </Block>
                <Block title={t('dashboard.stats.failed')} icon={FailedIcon} iconColor='red'>
                    {(o.numOfFailedOrders ?? 0).formatted()}
                </Block>
            </div>
        </>)
    }

    function content() {
        if (overview) return blocks(overview)
        if (query.loading) return <div className='py-16'><LoadingRows message={t('dashboard.loading')} /></div>
        if (query.error) return <div className='py-16'><EmptyState icon={ExpiredIcon} title={t('dashboard.error.title')} subtitle={query.error.message} /></div>
        return <div className='py-16'><EmptyState title={t('dashboard.empty.title')} /></div>
    }

    const inputClass = 'border border-gray-300 rounded shadow-sm p-2 sm:text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500'

    return (
        <>
            <PageHeader title={t('dashboard.title')}>
                <input type='date' className={inputClass} value={formatInputDateString(dateBegin)} onChange={(e) => setDateBegin(startOfDay(new Date(e.target.value)))}></input>
                <input type='date' className={inputClass} value={formatInputDateString(dateEnd)} onChange={(e) => setDateEnd(endOfDay(new Date(e.target.value)))}></input>
                {apps ? <AppsDropdown items={apps.apps} selected={app} onDidSelect={setApp} className='w-full sm:w-56' /> : null}
                <PrimaryButton className='w-full sm:w-32' onClick={() => query.refetch()}>
                    {isNetworkRequestInFlight(query.networkStatus) ? <SpinnerIcon /> : t('dashboard.head.buttons.refresh')}
                </PrimaryButton>
            </PageHeader>
            <div className='container mx-auto px-6'>
                {content()}
            </div>
        </>
    )
}